import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { ApolloError, gql, useQuery } from '@apollo/client';
import { InlineLoadingSpinner } from '@spins-gcp/shared-components-react';
import { NoAccessMessage } from '../no-access-message/no-access-message';

const LOGOUT_COUNTDOWN_SECONDS = 8;

// Any DAAP API call runs the login gate (internal users need full access; external users
// are limited to prod or the login allowlist). This one only checks that the gate lets us in.
export const DAAP_ACCESS_CHECK_QUERY = gql`
  query DaapAccessCheck {
    daapSubscribedApplications
  }
`;

// The API answers 403 directly, or through the gateway as a GraphQL error whose
// `extensions.response` carries the API's status.
export function isAccessDenied(error: ApolloError | undefined): boolean {
  if (!error) {
    return false;
  }
  const networkStatus = (error.networkError as { statusCode?: number } | null)
    ?.statusCode;
  if (networkStatus === 403) {
    return true;
  }
  return error.graphQLErrors.some(
    (e) =>
      (e.extensions?.['response'] as { status?: number } | undefined)
        ?.status === 403
  );
}

export function DaapAccessGuard({ children }: PropsWithChildren) {
  // skipMimic: a mimic 403 means "can't mimic that company", not "can't log in".
  // no-cache: keep this unmimicked result out of the cache the mimic-aware hooks read.
  const { loading, error } = useQuery(DAAP_ACCESS_CHECK_QUERY, {
    context: { skipMimic: true },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const denied = isAccessDenied(error);
  const [countdown, setCountdown] = useState(LOGOUT_COUNTDOWN_SECONDS);
  // The Apollo client is rebuilt when the mimic target or token changes, which reruns this
  // query. Only block rendering until the first answer, so the app isn't unmounted mid-session.
  const hasAnswered = useRef(false);
  if (!loading) {
    hasAnswered.current = true;
  }

  useEffect(() => {
    if (!denied) {
      return;
    }
    const interval = setInterval(() => {
      setCountdown((current) => Math.max(current - 1, 0));
    }, 1000);
    const timeout = setTimeout(() => {
      window.location.assign('/logout');
    }, LOGOUT_COUNTDOWN_SECONDS * 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [denied]);

  if (loading && !hasAnswered.current) {
    return <InlineLoadingSpinner />;
  }

  if (denied) {
    return (
      <NoAccessMessage
        message={`You do not have access to this application. Logging out in ${countdown}s.`}
      />
    );
  }

  return <>{children}</>;
}

export default DaapAccessGuard;
